// ============================================
// Channel List Component
// Displays joined and available channels
// ============================================

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useChat } from './ChatProvider';
import PresenceIndicator from './PresenceIndicator';
import { CreateChannelModal, BrowseChannelsModal, ArchivedChannelsModal } from './ChannelManagement';

/**
 * Channel list sidebar showing joined and available channels
 */
const ChannelList = ({ onChannelSelect }) => {
  const {
    channels,
    currentChannel,
    loadingChannels,
    joinChannel,
    leaveChannel,
    unreadCounts,
    fetchChannels,
    currentUser,
    searchUsers,
    startDM,
    getUserPresenceStatus
  } = useChat();

  const [joiningChannel, setJoiningChannel] = useState(null);
  const [leavingChannel, setLeavingChannel] = useState(null);
  const [showAvailable, setShowAvailable] = useState(false);
  const [showDMs, setShowDMs] = useState(true);

  // Channel management modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBrowseModal, setShowBrowseModal] = useState(false);
  const [showArchivedModal, setShowArchivedModal] = useState(false);

  // User search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [startingDMWith, setStartingDMWith] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const searchDebounceRef = useRef(null);

  // Calculate dropdown position when showing results
  useEffect(() => {
    if (showSearchResults && searchContainerRef.current) {
      const rect = searchContainerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
  }, [showSearchResults]);

  // Debounced search
  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear previous timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (query.length < 1) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    // Debounce the search
    searchDebounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      const results = await searchUsers(query);
      setSearchResults(results);
      setShowSearchResults(true);
      setIsSearching(false);
    }, 300);
  }, [searchUsers]);

  // Handle clicking on a search result to start DM
  const handleStartDMFromSearch = useCallback(async (user) => {
    setStartingDMWith(`${user.user_type}:${user.user_id}`);
    try {
      const result = await startDM(user.user_type, user.user_id, user.display_name);
      if (result) {
        // Clear search
        setSearchQuery('');
        setSearchResults([]);
        setShowSearchResults(false);

        // Find and switch to the new DM channel
        // Channels are refreshed by startDM, so we need to wait a moment
        setTimeout(() => {
          const dmChannel = channels.joined?.find(ch => ch.id === result.channelId);
          if (dmChannel) {
            onChannelSelect(dmChannel);
          }
        }, 100);
      }
    } finally {
      setStartingDMWith(null);
    }
  }, [startDM, channels.joined, onChannelSelect]);

  // Close search results when clicking outside (but not on the dropdown itself)
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if click is outside both the search input and dropdown
      const dropdown = document.getElementById('user-search-dropdown');
      const isOutsideInput = searchInputRef.current && !searchInputRef.current.contains(e.target);
      const isOutsideDropdown = !dropdown || !dropdown.contains(e.target);

      if (isOutsideInput && isOutsideDropdown) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Separate regular channels from DMs
  const { regularChannels, dmChannels } = useMemo(() => {
    const regular = [];
    const dms = [];

    (channels.joined || []).forEach(channel => {
      if (channel.is_dm) {
        dms.push(channel);
      } else {
        regular.push(channel);
      }
    });

    return { regularChannels: regular, dmChannels: dms };
  }, [channels.joined]);

  const handleJoinChannel = async (channel) => {
    setJoiningChannel(channel.id);
    try {
      const success = await joinChannel(channel.id);
      if (success) {
        onChannelSelect(channel);
      }
    } finally {
      setJoiningChannel(null);
    }
  };

  const handleLeaveChannel = async (e, channel) => {
    e.stopPropagation(); // Prevent selecting the channel
    if (window.confirm(`Leave #${channel.display_name}?`)) {
      setLeavingChannel(channel.id);
      try {
        await leaveChannel(channel.id);
      } finally {
        setLeavingChannel(null);
      }
    }
  };

  const handleChannelClick = (channel) => {
    onChannelSelect(channel);
  };

  // Get initials for DM avatar
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get avatar color based on user type
  const getAvatarColor = (userType) => {
    switch (userType) {
      case 'student':
        return 'bg-blue-500';
      case 'recruiter':
        return 'bg-green-500';
      case 'alumni':
        return 'bg-purple-500';
      case 'admin':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (loadingChannels) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Joined Channels */}
      <div className="p-2">
        <div className="flex items-center justify-between px-2 py-1.5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Channels</h3>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setShowBrowseModal(true)}
              className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600"
              title="Browse channels"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600"
              title="Create channel"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
            <button
              onClick={fetchChannels}
              className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600"
              title="Refresh"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {regularChannels.length === 0 ? (
          <p className="text-xs text-gray-400 px-2 py-2">No channels joined yet</p>
        ) : (
          <div className="space-y-1">
            {regularChannels.map(channel => (
              <div
                key={channel.id}
                onClick={() => handleChannelClick(channel)}
                className={`
                  w-full text-left px-2 py-2 rounded transition-colors group cursor-pointer
                  ${currentChannel?.id === channel.id
                    ? 'bg-blue-100 text-blue-800'
                    : 'hover:bg-gray-200 text-gray-700'
                  }
                `}
              >
                <div className="flex items-start space-x-2">
                  <span className="text-base flex-shrink-0">#</span>
                  <span className="text-sm font-medium break-words flex-1">{channel.display_name}</span>
                  {/* Unread badge */}
                  {unreadCounts[channel.id] > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center flex-shrink-0">
                      {unreadCounts[channel.id] > 99 ? '99+' : unreadCounts[channel.id]}
                    </span>
                  )}
                  {/* Leave button - appears on hover */}
                  <button
                    onClick={(e) => handleLeaveChannel(e, channel)}
                    disabled={leavingChannel === channel.id}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 hover:text-red-600 rounded transition-all flex-shrink-0"
                    title="Leave channel"
                  >
                    {leavingChannel === channel.id ? (
                      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Available Channels */}
      {channels.available.length > 0 && (
        <div className="p-2 border-t border-gray-200">
          <button
            onClick={() => setShowAvailable(!showAvailable)}
            className="flex items-center justify-between w-full px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700"
          >
            <span>Browse Channels</span>
            <svg
              className={`w-4 h-4 transition-transform ${showAvailable ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showAvailable && (
            <div className="mt-1 space-y-1">
              {channels.available.map(channel => (
                <div
                  key={channel.id}
                  className="px-2 py-2 rounded hover:bg-gray-200 group"
                >
                  <div className="flex items-start space-x-2">
                    <span className="text-base flex-shrink-0">#</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700 break-words">{channel.display_name}</p>
                      <p className="text-xs text-gray-400">{channel.member_count} members</p>
                    </div>
                  </div>
                  <div className="mt-1.5 pl-6">
                    <button
                      onClick={() => handleJoinChannel(channel)}
                      disabled={joiningChannel === channel.id}
                      className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50"
                    >
                      {joiningChannel === channel.id ? (
                        <span className="flex items-center space-x-1">
                          <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </span>
                      ) : (
                        'Join'
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Direct Messages section */}
      <div className="p-2 border-t border-gray-200">
        <button
          onClick={() => setShowDMs(!showDMs)}
          className="flex items-center justify-between w-full px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700"
        >
          <span>Direct Messages</span>
          <svg
            className={`w-4 h-4 transition-transform ${showDMs ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showDMs && (
          <div className="mt-1 space-y-1">
            {/* User Search Input */}
            <div className="px-2 mb-2" ref={searchInputRef}>
              <div className="relative" ref={searchContainerRef}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
                  placeholder="Search users..."
                  className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 pr-8"
                />
                {isSearching ? (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <svg className="animate-spin h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : (
                  <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                )}
              </div>
            </div>

            {/* Search Results Dropdown - Fixed position to float over everything */}
            {showSearchResults && searchResults.length > 0 && (
              <div
                id="user-search-dropdown"
                className="fixed z-[999999] w-[300px] bg-white border border-gray-200 rounded-md shadow-2xl max-h-48 overflow-y-auto"
                style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
              >
                {searchResults.map((user) => {
                  const isStarting = startingDMWith === `${user.user_type}:${user.user_id}`;
                  return (
                    <button
                      key={`${user.user_type}-${user.user_id}`}
                      onClick={() => handleStartDMFromSearch(user)}
                      disabled={isStarting}
                      className="w-full flex items-center space-x-2 px-3 py-2 hover:bg-gray-100 text-left disabled:opacity-50"
                    >
                      {/* Avatar with presence indicator */}
                      <div className="relative flex-shrink-0">
                        <div className={`w-7 h-7 rounded-full ${getAvatarColor(user.user_type)} flex items-center justify-center text-white text-xs font-medium`}>
                          {getInitials(user.display_name)}
                        </div>
                        <span className="absolute -bottom-0.5 -right-0.5">
                          <PresenceIndicator
                            status={getUserPresenceStatus(user.user_type, user.user_id)}
                            size="sm"
                            showBorder
                          />
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{user.display_name}</p>
                        {user.subtitle && (
                          <p className="text-xs text-gray-500 truncate">{user.subtitle}</p>
                        )}
                      </div>
                      {isStarting && (
                        <svg className="animate-spin h-4 w-4 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* No results message - Fixed position */}
            {showSearchResults && searchQuery.length > 0 && searchResults.length === 0 && !isSearching && (
              <div
                id="user-search-dropdown"
                className="fixed z-[999999] w-[300px] bg-white border border-gray-200 rounded-md shadow-2xl p-3"
                style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
              >
                <p className="text-xs text-gray-500 text-center">No users found</p>
              </div>
            )}

            {/* Existing DM list */}
            {dmChannels.length === 0 ? (
              <p className="text-xs text-gray-400 px-2 py-2">
                No direct messages yet.
                <br />
                <span className="text-gray-500">Search above or click on a user's avatar to start a conversation.</span>
              </p>
            ) : (
              dmChannels.map(channel => {
                const otherUser = channel.dm_other_participant || {};
                const displayName = otherUser.display_name || 'Unknown User';
                const userType = otherUser.type || 'user';

                return (
                  <div
                    key={channel.id}
                    onClick={() => handleChannelClick(channel)}
                    className={`
                      w-full text-left px-2 py-2 rounded transition-colors group cursor-pointer
                      ${currentChannel?.id === channel.id
                        ? 'bg-blue-100 text-blue-800'
                        : 'hover:bg-gray-200 text-gray-700'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-2">
                      {/* User avatar with presence indicator */}
                      <div className="relative flex-shrink-0">
                        <div className={`w-6 h-6 rounded-full ${getAvatarColor(userType)} flex items-center justify-center text-white text-xs font-medium`}>
                          {getInitials(displayName)}
                        </div>
                        {/* Presence indicator */}
                        <span className="absolute -bottom-0.5 -right-0.5">
                          <PresenceIndicator
                            status={getUserPresenceStatus(userType, otherUser.id)}
                            size="sm"
                            showBorder
                          />
                        </span>
                      </div>
                      <span className="text-sm font-medium break-words flex-1">{displayName}</span>
                      {/* Unread badge */}
                      {unreadCounts[channel.id] > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center flex-shrink-0">
                          {unreadCounts[channel.id] > 99 ? '99+' : unreadCounts[channel.id]}
                        </span>
                      )}
                    </div>
                    {/* Last message preview if available */}
                    {channel.last_activity_at && (
                      <p className="text-xs text-gray-400 mt-0.5 ml-8 truncate">
                        {new Date(channel.last_activity_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Archived Channels Button */}
      <div className="p-2 border-t border-gray-200">
        <button
          onClick={() => setShowArchivedModal(true)}
          className="w-full flex items-center space-x-2 px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
          <span>Archived Channels</span>
        </button>
      </div>

      {/* Channel Management Modals */}
      <CreateChannelModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={async (createdChannel) => {
          // Wait a moment for fetchChannels to complete, then find and switch to the new channel
          setTimeout(() => {
            const newChannel = channels.joined?.find(ch => ch.id === createdChannel.id);
            if (newChannel) {
              onChannelSelect(newChannel);
            }
          }, 500);
        }}
      />

      <BrowseChannelsModal
        isOpen={showBrowseModal}
        onClose={() => setShowBrowseModal(false)}
        onJoined={() => {
          // Channels list is refreshed automatically
        }}
      />

      <ArchivedChannelsModal
        isOpen={showArchivedModal}
        onClose={() => setShowArchivedModal(false)}
      />
    </div>
  );
};

export default ChannelList;
